import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  Input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActiviteInterneService } from '../../../providers/activite-interne/activite-interne.service';
import { NetworkProvider, NetworkStatus } from '../../../providers/network/network';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TypeOt } from 'src/app/mnt/common/modals/gft-filter/provider/gft-filter.provider';
import { AccesRapideItemComponent } from './acces-rapide-item/acces-rapide-item.component';
import { AgendaSaisieIndispoModal } from 'src/app/mnt/agenda/agenda-saisie-indispo-modal/agenda-saisie-indispo.modal';
import { getOnOffConfigOrElse } from '../../../providers/config/config.service';
import { TourneeProvider } from '../../../providers/activites/tournee.provider';
import { LoggerFactory } from '../../../providers/logger/logger-factory.service';
import { LoggerWrapper } from '../../../providers/logger/logger-wrapper.service';
import { UserGeolocConsentService } from '../../../providers/auth/user-geoloc-consent.service';
import { DemarrerActiviteInterneService } from '../../../providers/activite-interne/demarrer-activite-interne.service';
import { isToggleTopInterventionEnabled } from '../../../utils/toggle-utils';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'acces-rapide-widget',
  templateUrl: 'acces-rapide-widget.component.html',
  styleUrls: ['acces-rapide-widget.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, IonicModule, FormsModule, TranslateModule, AccesRapideItemComponent],
})
export class AccesRapideWidgetComponent implements OnInit {
  @Input() resetSubject: Observable<boolean> = new Observable<boolean>();

  private readonly networkProvider = inject(NetworkProvider);
  private readonly networkStatus = toSignal(this.networkProvider.networkStatusSubject);
  readonly isOnline = computed(() => this.networkStatus() === NetworkStatus.ONLINE);

  isLoading: WritableSignal<boolean> = signal<boolean>(false);
  hasOtReleveP1: WritableSignal<boolean> = signal<boolean>(false);
  private logger: LoggerWrapper;

  constructor(
    private router: Router,
    private activiteInterneService: ActiviteInterneService,
    private demarrerActiviteInterneService: DemarrerActiviteInterneService,
    private userGeolocConsentService: UserGeolocConsentService,
    private modalController: ModalController,
    private tourneeProvider: TourneeProvider,
    private loggerFactory: LoggerFactory
  ) {
    this.logger = this.loggerFactory.getInstance(this, true);
  }

  async ngOnInit() {
    await this.refresh();

    this.resetSubject.subscribe((response: boolean): void => {
      if (response) {
        this.refresh();
      }
    });
  }

  private async refresh() {
    this.isLoading.set(true);
    try {
      this.hasOtReleveP1.set((await this.tourneeProvider.getOtsP1Semaine()).length > 0);
      this.isLoading.set(false);
    } catch (error) {
      this.hasOtReleveP1.set(false);
      this.isLoading.set(false);
    }
  }

  async startActiviteInterneRenfort() {
    if (this.isOnline()) {
      if (this.hasGeolocConsent()) {
        await this.demarrerActiviteInterneService.startActiviteInterne('renfort');
      } else {
        await this.activiteInterneService.startActiviteInterne(true);
      }
    }
  }

  async startActiviteInterne() {
    if (this.isOnline()) {
      if (this.hasGeolocConsent()) {
        await this.demarrerActiviteInterneService.startActiviteInterne('activite-interne');
      } else {
        await this.activiteInterneService.startActiviteInterne(false);
      }
    }
  }

  async startIndisponibilite() {
    if (this.isOnline()) {
      let modal: HTMLIonModalElement = await this.modalController.create({
        component: AgendaSaisieIndispoModal,
      });
      await modal.present();
    }
  }

  async goToPlanningEquipe() {
    if (this.isOnline()) {
      await this.router.navigate(['/mnt/agenda/equipe']);
    }
  }

  async goToTournee(typeOt: TypeOt) {
    await this.router.navigate(['/mnt/round'], {
      queryParams: { segment: 'semaine', typeOt: typeOt },
    });
    this.logger.info('Type OTs selectionnés depuis accès rapide: ', typeOt);
  }

  async goToRelevesP1() {
    if (this.isToggleOtsP1Enabled()) {
      await this.router.navigate(['/mnt/releves-p1']);
    } else {
      await this.goToTournee('releve');
    }
  }

  async goToDemandeTravaux() {
    if (this.isOnline()) {
      await this.router.navigate(['/mnt/travaux/create']);
    }
  }

  isToggleOtsP1Enabled(): boolean {
    return getOnOffConfigOrElse('TOGGLE_PAGE_OT_RELEVE_P1', 'OFF') === 'ON';
  }

  hasGeolocConsent(): boolean {
    return this.userGeolocConsentService.getCurrentConsentChrono();
  }

  async goToSuggestOt() {
    if (this.isOnline()) {
      await this.router.navigate(['/mnt/suggest-ot']);
    }
  }

  async goToTopIntervention() {
    await this.router.navigate(['/mnt/top-intervention']);
  }

  protected readonly isToggleTopInterventionEnabled = isToggleTopInterventionEnabled;
}
